/**
 * AuthChrome — shared wrapper for all auth screens (Login / Activate / Setup2fa).
 *
 * Renders the full-viewport `bg-bg-primary` canvas, centers content, and
 * provides the theme-toggle + language-toggle controls in the top-right corner.
 * All three auth pages import this instead of duplicating the controls.
 */

import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Moon, Sun } from "lucide-react";
import { useTheme } from "@/lib/theme";
import { cn } from "@/lib/cn";

interface AuthChromeProps {
  children: ReactNode;
  /** Extra classes on the centering wrapper (e.g. `py-12` for tall Setup2fa). */
  className?: string;
}

export function AuthChrome({ children, className }: AuthChromeProps) {
  const { t, i18n } = useTranslation();
  const { theme, toggle: toggleTheme } = useTheme();

  const toggleLang = () => {
    void i18n.changeLanguage(i18n.language === "ru" ? "en" : "ru");
  };

  return (
    <div className="relative min-h-dvh bg-bg-primary">
      {/* Top-right chrome controls */}
      <div className="absolute right-4 top-4 flex items-center gap-1">
        <button
          type="button"
          onClick={toggleLang}
          aria-label={t("app.toggle_language")}
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-lg text-xs font-semibold",
            "text-fg-muted transition-colors hover:bg-bg-elevated hover:text-fg-primary",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
          )}
        >
          {i18n.language === "ru" ? "EN" : "RU"}
        </button>
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={t("app.toggle_theme")}
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-lg",
            "text-fg-muted transition-colors hover:bg-bg-elevated hover:text-fg-primary",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
          )}
        >
          {theme === "dark" ? (
            <Sun aria-hidden="true" size={16} strokeWidth={2} />
          ) : (
            <Moon aria-hidden="true" size={16} strokeWidth={2} />
          )}
        </button>
      </div>

      {/* Centered content */}
      <main
        className={cn(
          "flex min-h-dvh items-center justify-center px-4 py-12",
          className,
        )}
      >
        {children}
      </main>
    </div>
  );
}
